import React, { useState, useEffect, useRef } from "react";
import {
  FaDumbbell,
  FaRunning,
  FaHeartbeat,
  FaStar,
  FaLeaf,
} from "react-icons/fa"; // Dodano FaLeaf jako ikonę rośliny
import { motion } from "framer-motion";
import img1 from "../../../assets/FOTO/IMG_7302.JPG";
import img2 from "../../../assets/FOTO/IMG_7879.png";
import img4 from "../../../assets/FOTO/IMG_8404.png";
import img5 from "../../../assets/FOTO/IMG_7264.JPG";

const Program = () => {
  const [isVisible, setIsVisible] = useState(false);
  const programRef = useRef(null);

  const questions = [
    {
      title: "Siła",
      text: "Uwierz w swoją moc.",
      icon: <FaDumbbell className="text-3xl" />,
    },
    {
      title: "Wytrwałość",
      text: "Nigdy się nie poddawaj.",
      icon: <FaRunning className="text-3xl" />,
    },
    {
      title: "Determinacja",
      text: "Każdy dzień to szansa.",
      icon: <FaHeartbeat className="text-3xl" />,
    },
    {
      title: "Motywacja",
      text: "Jesteś bliżej celu.",
      icon: <FaStar className="text-3xl" />,
    },
  ];

  // Intersection Observer for scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect(); // Stop observing once the element is visible
        }
      },
      { threshold: 0.2 } // Trigger when 20% of the element is visible
    );

    if (programRef.current) {
      observer.observe(programRef.current);
    }

    return () => {
      if (programRef.current) {
        observer.unobserve(programRef.current);
      }
    };
  }, []);

  return (
    <section
      ref={programRef}
      className="bg-[#191919] text-white p-8 pt-[100px] flex flex-col items-center"
    >
      {/* Title */}
      <motion.h1
        className="text-5xl font-bold text-center mb-4 uppercase"
        initial={{ opacity: 0, y: -50 }}
        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8 }}
      >
        Oferta{" "}
      </motion.h1>

      {/* Description */}
      <motion.p
        className="text-center mb-8 max-w-xl mt-5 text-gray-300"
        initial={{ opacity: 0, y: 50 }}
        animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        Oferujemy różnorodne programy dostosowane do Twoich potrzeb. Nasze
        podejście łączy nowoczesne metody treningowe z indywidualnym podejściem
        do klienta.
      </motion.p>

      {/* Programs */}
      <div className="flex justify-center space-x-4 mb-8 mt-8 md:flex-row flex-col gap-4">
        {[
          {
            title: "Trening EMS",
            description:
              "Jest to trening 1:1 z trenerem z użyciem elektrostymulacji. Dzięki niej oszczędzamy dużo czasu, ponieważ trening trwa jedynie 20 minut, a zaoszczędzony czas możesz poświęcić rodzinie lub/i znajomym. W zależności od celu możemy spalać kalorie, budować masę mięśniową, działać rehabilitacyjnie.",
            imgSrc: img1,
            alt: "Trening EMS",
          },
          {
            title: "Trening Personalny",
            description:
              "Indywidualne podejście do treningu, dostosowane do Twoich celów.",
            imgSrc: img2,
            alt: "Trening Personalny",
          },
          {
            title: "Drenaż limfatyczny",
            description: (
              <>
                Inaczej presoterapia to nasze miejsce odpoczynku, tutaj
                zrelaksujesz się przy kawie lub herbacie 🥰
                <p>W czym pomaga drenaż?</p>
                <ul className="list-none mt-4 space-y-2">
                  <li className="flex items-center gap-2">
                    <FaLeaf className="text-green-500" /> {/* Ikona rośliny */}
                    Redukcja obrzęków i poprawa krążenia.
                  </li>
                  <li className="flex items-center gap-2">
                    <FaLeaf className="text-green-500" />
                    Detoksykacja organizmu.
                  </li>
                  <li className="flex items-center gap-2">
                    <FaLeaf className="text-green-500" />
                    Poprawa wyglądu skóry.
                  </li>
                  <li className="flex items-center gap-2">
                    <FaLeaf className="text-green-500" />
                    Relaks i odprężenie.
                  </li>
                </ul>
              </>
            ),
            imgSrc: img4,
            alt: "Drenaż limfatyczny",
          },
        ].map((item, index) => (
          <motion.div
            key={index}
            className="flex flex-col items-center h-full w-full"
            initial={{ opacity: 0, y: 50 }}
            animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.8, delay: 0.3 * index }}
          >
            <div className="relative overflow-hidden w-[350px] h-[250px] mb-2">
              <img
                src={item.imgSrc}
                alt={item.alt}
                className="object-cover w-full h-full transition-transform duration-300 ease-in-out transform hover:scale-110"
              />
            </div>
            <h2 className="text-2xl font-semibold uppercase">{item.title}</h2>
            <motion.p
              className="text-center text-gray-300 px-6"
              initial={{ opacity: 0 }}
              animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.8, delay: 0.3 + 0.2 * index }}
            >
              {item.description}
            </motion.p>
          </motion.div>
        ))}
      </div>

      {/* Motivational section with icons */}
      <motion.div
        className="flex md:flex-row flex-col-reverse justify-between items-center mb-8 mt-[150px] gap-4"
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Image section */}
        <div className="w-full md:w-1/2 flex justify-center mb-8 md:mb-0">
          <img
            src={img5}
            alt="Drenaż limfatyczny"
            className="w-full h-full transition-transform duration-300 ease-in-out transform hover:scale-110"
          />
        </div>

        {/* Grid of Icons */}
        <div className="grid grid-cols-2 md:grid-cols-2 gap-8 p-2 w-full">
          {questions.map((item, index) => (
            <div
              key={index}
              className="bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <motion.div
                className="flex flex-col items-center justify-center transition-all duration-300 transform gap-3"
                initial={{ opacity: 0, y: 50 }}
                animate={
                  isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }
                }
                transition={{ duration: 0.6, delay: 0.2 * index }}
              >
                <div className="text-white mb-2 bg-gray-700 w-20 h-20 rounded-full hover:bg-orange-500 items-center flex justify-center">
                  {item.icon}
                </div>
                <h3 className="text-2xl font-bold uppercase text-white">
                  {item.title}
                </h3>
                <motion.p
                  className="text-mf text-center text-gray-300"
                  initial={{ opacity: 0 }}
                  animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
                  transition={{ duration: 0.8, delay: 0.3 + 0.2 * index }}
                >
                  {item.text}
                </motion.p>
              </motion.div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Program;
